<script lang="ts" setup>
import Skeleton from "primevue/skeleton"
import ProgressSpinner from "primevue/progressspinner"
import { secondsToHhMmSs } from "@/utils";
import capitalize from 'lodash/capitalize'
import { computed } from 'vue'
import LwChart from "./charts/LwChart.vue";
import type { OhlcvTuple } from "@/utils/types";
import type { MarketType } from "@/utils/types";

export interface OhlcvChartProps {
  marketType?: MarketType | null
  ohlcv?: OhlcvTuple[] | null
  exchange?: string
  title?: string
  titleHref?: string
  quoteAsset?: string
  titleClass?: string | string[]
  vol24h?: string | null
  fundingRate?: number | null
  fundingPeriod?: number | null
  loading?: boolean,
  error?: boolean,
  vol25hError?: boolean | null,
  fundingError?: boolean | null,
  hideVol24h?: boolean,
  hideFundingRate?: boolean
  yFormat?: '1s' | null
  minMove: number
  height?: string
}

const props = defineProps<OhlcvChartProps>()

const ohlcv = computed(() => {
  return props.ohlcv?.map(x => {
    return [
      Number(x[0]),
      Number(x[1]),
      Number(x[2]),
      Number(x[3]),
      Number(x[4]),
    ]
  }) ?? []
})

const showFundingRate = computed(() => {
  return (
    props.fundingRate !== undefined &&
    props.fundingPeriod !== undefined &&
    props.fundingRate !== null &&
    props.fundingPeriod !== null &&
    !props.fundingPeriod?.toString().startsWith('-')
  )
})
const cardTitle = computed(() => props.exchange ? `${capitalize(props.exchange)} / ${(props.marketType == 'spot' ? 's' : 'f')}` : props.title)
</script>

<template>
  <div>
    <div class="flex justify-between chart-title py-2 px-4">

      <!-- Биржа -->
      <div class="flex justify-start col-span-2 md:col-span-1 items-center">
        <Skeleton height="30px" width="100px" v-if="!cardTitle"></Skeleton>
        <template v-else>
          <a v-if="titleHref" :class="props.titleClass" class="pit hover" :href="titleHref" target="_blank">{{ cardTitle
          }}</a>
          <span :class="props.titleClass" class="pit" v-else>{{ cardTitle }}</span>
        </template>
        <span v-if="Boolean(quoteAsset)" class="me-2 pit danger">{{ quoteAsset }}</span>
      </div>

      <!-- СОТ -->
      <div class="flex col-span-1 md:col-span-1 items-center"
        :class="{ 'md:col-start-3 justify-end': hideFundingRate, 'justify-center': !hideFundingRate }"
        v-if="hideVol24h !== true">
        <span class="pit digit" v-if="Boolean(vol24h)">${{ vol24h }}</span>
        <Skeleton height="30px" width="100px" v-else></Skeleton>
      </div>

      <!-- Фандинг -->
      <div class="flex col-span-3 md:col-span-1 justify-end items-center" v-if="hideFundingRate !== true">
        <span class="pit digit" v-if="showFundingRate">
          {{ Number(fundingRate).toFixed(4) }}% ({{ secondsToHhMmSs(fundingPeriod ?? 0) }})
        </span>
        <Skeleton height="30px" width="100px" v-else></Skeleton>
      </div>
    </div>

    <div v-if="props.loading" :style="`height: ${props.height ?? '400px'}`" class="flex items-center justify-center">
      <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" animationDuration=".5s" />
    </div>

    <template v-else>
      <LwChart :y-format="yFormat" v-if="ohlcv && ohlcv.length > 0" :data="ohlcv" type="ohlcv"
        :height="props.height ?? '400px'" :min-move="props.minMove">
      </LwChart>
      <!-- Показывать ошибку только если данных нет -->
      <div v-else-if="props.error" :style="`height: ${height}`" class="flex items-center justify-center sr-text">
        {{ $t('loadingError') }}
      </div>
      <div v-else :style="`height: ${height}`" class="flex items-center justify-center sr-text">
        {{ $t('noData') }}
      </div>
    </template>
  </div>
</template>
